import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Box, Button, Dialog, DialogTitle, IconButton, Menu, MenuItem, Popover, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import { chapterAt, formatClock, languageLabel } from "../helpUtils";
import { getHelpPlayUrls } from "../services";
import { HelpPlayUrls, HelpVideo } from "../types";
import useVideoPlayer from "../useVideoPlayer";
import SeekBar from "./SeekBar";
import { TimelineList, TimelineToggle } from "./Timeline";

const SEEK_STEP_SECONDS = 5;
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const HEADER_AND_CONTROLS_PX = 150;
const TIMELINE_WIDTH = 300;
const TIMELINE_MAX_HEIGHT = 360;
/** Fresh signed links asked for after load errors (e.g. links expired while paused) before giving up. */
const MAX_LINK_REFRESHES = 2;
const LINK_RETRY_DELAY_MS = 1100;
// Menus open upward from the control bar so the dialog never has to grow or scroll.
const ABOVE_ANCHOR = {
  anchorOrigin: { vertical: "top", horizontal: "right" },
  transformOrigin: { vertical: "bottom", horizontal: "right" },
} as const;

interface FullPlayerProps {
  video: HelpVideo;
  start?: number;
  end?: number;
  onProgress: (seconds: number) => void;
  /** Esc, backdrop or the close action; progress is kept. */
  onClose: (seconds: number) => void;
}

const FullPlayer: FC<FullPlayerProps> = ({ video, start, end, onProgress, onClose }) => {
  const { ref, time, paused, seek, toggle, keepPosition, handlers, currentTime } = useVideoPlayer({ start, end, onProgress });
  const { pathname } = useLocation();
  const [urls, setUrls] = useState<HelpPlayUrls>();
  const [unavailable, setUnavailable] = useState(false);
  const refreshes = useRef(0);
  const [timelineAnchor, setTimelineAnchor] = useState<HTMLElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [speedAnchor, setSpeedAnchor] = useState<HTMLElement | null>(null);
  const [shareAnchor, setShareAnchor] = useState<HTMLElement | null>(null);
  const [languageAnchor, setLanguageAnchor] = useState<HTMLElement | null>(null);
  const current = chapterAt(video.chapters, time);
  const duration = video.durationSec;

  const retryTimer = useRef<number>();

  // Signed links are fetched per play (the first call is the usage count); `from` is the page it was played on.
  const loadUrls = useCallback(
    (refresh = false) =>
      getHelpPlayUrls(video.id, video.locale, pathname, refresh)
        .then(setUrls)
        .catch(() => setUnavailable(true)),
    // The page it was opened on; navigating while open doesn't reload the video.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [video.id, video.locale]
  );

  useEffect(() => {
    void loadUrls();
    return () => window.clearTimeout(retryTimer.current);
  }, [loadUrls]);

  // The CDN answers any refused request (expired or bad link) with an error page, so a load error
  // gets fresh links, resuming at the same position, a couple of times before giving up. The wait
  // matters: a link signed in the same second is identical, and an unchanged src never reloads.
  const onMediaError = () => {
    if (refreshes.current >= MAX_LINK_REFRESHES) {
      setUnavailable(true);
      return;
    }
    refreshes.current += 1;
    keepPosition();
    retryTimer.current = window.setTimeout(() => void loadUrls(true), LINK_RETRY_DELAY_MS);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const element = ref.current;
    if (!element || event.target instanceof HTMLButtonElement || event.target instanceof HTMLLIElement) return;
    if (event.key === " ") {
      event.preventDefault();
      toggle();
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      // No length until the file's metadata arrives; seeking before that would throw.
      if (!Number.isFinite(element.duration)) return;
      const delta = event.key === "ArrowLeft" ? -SEEK_STEP_SECONDS : SEEK_STEP_SECONDS;
      element.currentTime = Math.min(element.duration, Math.max(0, element.currentTime + delta));
    }
  };

  const changeSpeed = (value: number) => {
    setSpeed(value);
    if (ref.current) {
      // The default rate survives a source reload (link refresh); playbackRate alone resets to it.
      ref.current.defaultPlaybackRate = value;
      ref.current.playbackRate = value;
    }
    setSpeedAnchor(null);
  };

  const copyLink = (seconds?: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set("help", video.id);
    url.searchParams.delete("t");
    if (seconds !== undefined) url.searchParams.set("t", String(Math.floor(seconds)));
    setShareAnchor(null);
    navigator.clipboard
      .writeText(url.toString())
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Could not copy the link"));
  };

  return (
    <Dialog
      open
      onClose={() => onClose(currentTime())}
      onKeyDown={onKeyDown}
      maxWidth={false}
      PaperProps={{
        sx: {
          // 16:9 video plus the header and control bar must fit the viewport height.
          width: `min(1200px, 94vw, calc((92vh - ${HEADER_AND_CONTROLS_PX}px) * 16 / 9))`,
          maxWidth: "none",
          m: 1,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>
          {video.title}
        </Box>
        <IconButton size="small" aria-label="Close" onClick={() => onClose(currentTime())}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box>
        {unavailable ? (
          <Box sx={{ aspectRatio: "16 / 9", bgcolor: "grey.900", display: "grid", placeItems: "center", px: 3 }}>
            <Typography color="common.white" textAlign="center">
              This video is not available right now. Please try again later.
            </Typography>
          </Box>
        ) : (
          <Box
            component="video"
            ref={ref}
            src={urls?.videoUrl}
            poster={urls?.posterUrl ?? video.posterUrl}
            // The CDN is another origin: CORS mode so the chapters track loads.
            crossOrigin="anonymous"
            muted={muted}
            playsInline
            onClick={toggle}
            onError={onMediaError}
            onPlaying={() => {
              refreshes.current = 0;
            }}
            sx={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "contain", bgcolor: "grey.900", cursor: "pointer" }}
            {...handlers}
          >
            {urls?.chaptersUrl && <track kind="chapters" src={urls.chaptersUrl} default />}
          </Box>
        )}
        <Box sx={{ px: 2 }}>
          <SeekBar src={urls?.videoUrl} duration={duration} time={time} chapters={video.chapters} onSeek={seek} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <IconButton aria-label={paused ? "Play" : "Pause"} onClick={toggle}>
            {paused ? <PlayArrowIcon /> : <PauseIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
            {formatClock(time)} / {formatClock(duration)}
          </Typography>
          <Typography variant="body2" noWrap color="text.secondary" sx={{ flex: 1 }}>
            {current?.title}
          </Typography>
          <TimelineToggle count={video.chapters.length} open={!!timelineAnchor} onClick={(event) => setTimelineAnchor(event.currentTarget)} />
          <Popover anchorEl={timelineAnchor} open={!!timelineAnchor} onClose={() => setTimelineAnchor(null)} {...ABOVE_ANCHOR}>
            <Box sx={{ width: TIMELINE_WIDTH, maxHeight: TIMELINE_MAX_HEIGHT, overflowY: "auto", p: 0.5 }}>
              <TimelineList
                chapters={video.chapters}
                current={current}
                onSelect={(chapter) => {
                  setTimelineAnchor(null);
                  seek(chapter.start);
                }}
              />
            </Box>
          </Popover>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TranslateIcon />}
            aria-haspopup="menu"
            onClick={(event) => setLanguageAnchor(event.currentTarget)}
            
          >
            {languageLabel(video.locale)} ▾
          </Button>
          <Menu anchorEl={languageAnchor} open={!!languageAnchor} onClose={() => setLanguageAnchor(null)} {...ABOVE_ANCHOR}>
            <MenuItem selected onClick={() => setLanguageAnchor(null)}>
              ✓ {languageLabel(video.locale)}
            </MenuItem>
            <MenuItem disabled>More languages soon</MenuItem>
          </Menu>
          <Button size="small" variant="outlined" onClick={(event) => setSpeedAnchor(event.currentTarget)} sx={{ minWidth: 0 }}>
            {speed}x
          </Button>
          <Menu anchorEl={speedAnchor} open={!!speedAnchor} onClose={() => setSpeedAnchor(null)} {...ABOVE_ANCHOR}>
            {SPEEDS.map((value) => (
              <MenuItem key={value} selected={value === speed} onClick={() => changeSpeed(value)}>
                {value}x
              </MenuItem>
            ))}
          </Menu>
          <Button size="small" variant="outlined" startIcon={<ShareOutlinedIcon />} aria-haspopup="menu" onClick={(event) => setShareAnchor(event.currentTarget)} >
            Share
          </Button>
          <Menu anchorEl={shareAnchor} open={!!shareAnchor} onClose={() => setShareAnchor(null)} {...ABOVE_ANCHOR}>
            <MenuItem onClick={() => copyLink(currentTime())}>Copy link to this moment ({formatClock(time)})</MenuItem>
            <MenuItem onClick={() => copyLink()}>Copy video link</MenuItem>
          </Menu>
          <IconButton aria-label={muted ? "Unmute" : "Mute"} onClick={() => setMuted((value) => !value)}>
            {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
          <IconButton aria-label="Fullscreen" onClick={() => void ref.current?.requestFullscreen?.()}>
            <FullscreenIcon />
          </IconButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FullPlayer;
